import styled from 'styled-components';
import { useListDocumentRulesQuery } from '@shared/api';
import { Modal } from '@shared/ui/Modal';
import { Loader } from '@shared/ui/Progress';
import { Badge } from '@shared/ui/Badge';
import type { KnowledgeDocument, ChecklistRule } from '@entities/knowledge/types';

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
`;

const Intro = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  line-height: 1.55;
`;

const RuleCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3.5)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const RuleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const RuleNum = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Category = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Requirement = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

// Дополнительные факты правила: риск нарушения и обоснование эталона.
const Extra = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ExtraLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Empty = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(6)};
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
`;

interface Props {
  open: boolean;
  document: KnowledgeDocument | null;
  onClose: () => void;
}

const SEVERITY_LABEL: Record<ChecklistRule['severity'], string> = {
  critical: 'Критично',
  major: 'Существенно',
  minor: 'Незначительно',
};

// tone-маппинг для severity-бейджа: critical→danger, major→warning, minor→neutral.
function severityTone(s: ChecklistRule['severity']): 'danger' | 'warning' | 'neutral' {
  switch (s) {
    case 'critical':
      return 'danger';
    case 'major':
      return 'warning';
    case 'minor':
      return 'neutral';
  }
}

export function ChecklistRulesModal({ open, document, onClose }: Props) {
  const { data, isLoading } = useListDocumentRulesQuery(document ? document.id : '', {
    skip: !document,
  });

  if (!document) return null;

  const items = data?.items ?? [];

  return (
    <Modal open={open} title={`Правила анализа: ${document.title}`} onClose={onClose}>
      <Header>
        <Badge>Чек-лист</Badge>
        {document.version && <span>v{document.version}</span>}
        <span>· {items.length} правил извлечено</span>
      </Header>

      <Intro>
        Это регламент проверки, который агент извлёк из документа и будет применять при экспертизе.
        Каждое правило — то, что агент «запомнил»: категория, требование и связанный риск.
      </Intro>

      {isLoading && <Loader />}

      {data && items.length === 0 && (
        <Empty>Правила ещё не извлечены — документ индексируется.</Empty>
      )}

      {items.map((r) => (
        <RuleCard key={r.id}>
          <RuleMeta>
            <RuleNum>#{r.idx}</RuleNum>
            <Badge $tone={severityTone(r.severity)}>{SEVERITY_LABEL[r.severity]}</Badge>
            <Category>{r.category}</Category>
          </RuleMeta>
          <Requirement>{r.requirement}</Requirement>
          {r.risk && (
            <Extra>
              <ExtraLabel>Риск: </ExtraLabel>
              {r.risk}
            </Extra>
          )}
          {r.rationale && (
            <Extra>
              <ExtraLabel>Обоснование: </ExtraLabel>
              {r.rationale}
            </Extra>
          )}
        </RuleCard>
      ))}
    </Modal>
  );
}
